import { create } from 'zustand'
import { TAG_GROUPS } from '@/features/discover/types/tags'
import { digestEditor } from '@/features/editor/lib/editor-digest'
import { buildDigestInput } from '@/features/editor/lib/editor-digest-input'
import {
  createEmptyDraft,
  createEmptyEntry,
} from '@/features/editor/lib/editor-factories'
import { normalizeEntry } from '@/features/editor/lib/editor-normalize'
import {
  normalizeTags,
  type ExclusiveTagGroupKey,
} from '@/features/editor/lib/editor-tags'
import type {
  EditorDraft,
  EditorKnowledge,
  EditorPublishStatus,
  EditorReviewMeta,
} from '@/features/editor/types/editor.types'
import type {
  KnowledgeEntry,
  KnowledgeKind,
} from '@/features/editor/types/knowledge.types'

export type EditorState = {
  draft: EditorDraft
  knowledge: EditorKnowledge
  reviewMeta: EditorReviewMeta

  publishStatus: EditorPublishStatus
  saving: boolean
  publishing: boolean
  unpublishing: boolean

  // 草稿（保存）基线
  lastSavedDigest: string | null
  // 线上（发布）基线：用于判断“已发布但未同步到线上”
  lastPublishedDigest: string | null

  init: (engineId?: string | null, opts?: { markSaved?: boolean }) => void
  hydrate: (
    data: {
      draft: EditorDraft
      knowledge?: Partial<EditorKnowledge>
      publishStatus?: EditorPublishStatus
      reviewMeta?: EditorReviewMeta
    },
    opts?: { markSaved?: boolean }
  ) => void

  patchDraft: (patch: Partial<EditorDraft>) => void
  setTags: (tags: string[]) => void
  toggleTag: (tag: string) => void
  selectExclusiveTag: (groupKey: ExclusiveTagGroupKey, tag: string) => void

  addEntry: (kind: KnowledgeKind, seed?: Partial<KnowledgeEntry>) => string
  removeEntry: (kind: KnowledgeKind, id: string) => void
  patchEntry: (
    kind: KnowledgeKind,
    id: string,
    patch: Partial<KnowledgeEntry>
  ) => void

  toggleEntryEnabled: (kind: KnowledgeKind, id: string) => void
  //  toggleEntryCollapsed removed

  moveEntry: (kind: KnowledgeKind, fromIndex: number, toIndex: number) => void

  setSaving: (saving: boolean) => void
  setPublishing: (publishing: boolean) => void
  setUnpublishing: (unpublishing: boolean) => void
  setPublishStatus: (status: EditorPublishStatus) => void

  // 保存成功：只更新草稿基线
  markSaved: () => void
  // 发布成功：更新线上基线（通常也会同时 markSaved）
  markPublished: () => void
  // 下架成功：清空线上基线
  markUnpublished: () => void

  reset: () => void
}

function createInitialKnowledge(): EditorKnowledge {
  return {
    worldInfo: [],
    characterProfiles: [],
  }
}

function createInitialReviewMeta(): EditorReviewMeta {
  return {
    lastReviewNote: undefined,
    lastReviewedAt: undefined,
    updatedAt: undefined,
    hasPendingReview: false,
  }
}

function createInitialState(engineId: string | null = null) {
  return {
    draft: createEmptyDraft(engineId),
    knowledge: createInitialKnowledge(),
    reviewMeta: createInitialReviewMeta(),
    publishStatus: 'draft' as const,
    saving: false,
    publishing: false,
    unpublishing: false,
    lastSavedDigest: null as string | null,
    lastPublishedDigest: null as string | null,
  }
}

function computeDigest(s: Pick<EditorState, 'draft' | 'knowledge'>) {
  return digestEditor(buildDigestInput(s.draft, s.knowledge))
}

function normalizeUrl(v: unknown): string | undefined {
  if (v === undefined) return undefined
  if (typeof v !== 'string') return undefined
  return v.trim() // 允许返回 ''
}

function normalizeDraftPatch(
  patch: Partial<EditorDraft>
): Partial<EditorDraft> {
  const next: Partial<EditorDraft> = { ...patch }
  if ('coverImageUrl' in next)
    next.coverImageUrl = normalizeUrl(next.coverImageUrl)
  if ('backgroundImageUrl' in next)
    next.backgroundImageUrl = normalizeUrl(next.backgroundImageUrl)
  if ('tags' in next) next.tags = normalizeTags(next.tags)
  return next
}

export const useEditorStore = create<EditorState>()((set, get) => ({
  ...createInitialState(null),

  init: (engineId, opts) => {
    set(() => {
      const next = {
        ...createInitialState(engineId ?? null),
      }

      const markSaved = opts?.markSaved ?? true
      return markSaved
        ? { ...next, lastSavedDigest: computeDigest(next) }
        : next
    })
  },

  hydrate: (data, opts) => {
    set((s) => {
      const nextDraft = { ...data.draft }

      const worldInfo = (
        data.knowledge?.worldInfo ?? s.knowledge.worldInfo
      ).map(normalizeEntry)
      const characterProfiles = (
        data.knowledge?.characterProfiles ?? s.knowledge.characterProfiles
      ).map(normalizeEntry)

      const next = {
        ...s,
        draft: nextDraft,
        knowledge: { worldInfo, characterProfiles },
        publishStatus: data.publishStatus ?? s.publishStatus,
        reviewMeta: data.reviewMeta ?? s.reviewMeta,
      }

      const markSaved = opts?.markSaved ?? true
      if (!markSaved) return next

      const d = computeDigest(next)
      const published = next.publishStatus === 'published'
      const hasPublishedBaseline = s.lastPublishedDigest != null

      return {
        ...next,
        lastSavedDigest: d,

        // ✅ 关键修复：
        // hydrate 会在「首次加载」和「保存后 invalidateQueries 重新拉取」都触发。
        // - 首次加载：还没有线上基线 -> 初始化 lastPublishedDigest
        // - 保存后回流：绝对不能覆盖线上基线，否则“已发布·有更新待审核”会被抹平
        lastPublishedDigest:
          published && !hasPublishedBaseline ? d : s.lastPublishedDigest,
      }
    })
  },

  patchDraft: (patch) => {
    const normalized = normalizeDraftPatch(patch)
    set((s) => ({ ...s, draft: { ...s.draft, ...normalized } }))
  },

  setTags: (tags) => {
    const next = normalizeTags(tags)
    set((s) => ({ ...s, draft: { ...s.draft, tags: next } }))
  },

  toggleTag: (tag) => {
    if (!tag) return
    set((s) => {
      const tags = s.draft.tags ?? []
      const next = tags.includes(tag)
        ? tags.filter((t) => t !== tag)
        : [...tags, tag]
      return { ...s, draft: { ...s.draft, tags: normalizeTags(next) } }
    })
  },

  selectExclusiveTag: (groupKey, tag) => {
    const group = TAG_GROUPS[groupKey]
    const groupTags = group.tags as string[]
    if (!groupTags.includes(tag)) return
    set((s) => {
      const tags = s.draft.tags ?? []
      const otherTags = tags.filter((t) => !groupTags.includes(t))
      const next = normalizeTags([...otherTags, tag])
      return { ...s, draft: { ...s.draft, tags: next } }
    })
  },

  addEntry: (kind, seed) => {
    const entry = createEmptyEntry(seed)
    set((s) => ({
      ...s,
      knowledge: {
        ...s.knowledge,
        [kind]: [entry, ...s.knowledge[kind]],
      },
    }))
    return entry.id
  },

  removeEntry: (kind, id) => {
    set((s) => ({
      ...s,
      knowledge: {
        ...s.knowledge,
        [kind]: s.knowledge[kind].filter((e) => e.id !== id),
      },
    }))
  },

  patchEntry: (kind, id, patch) => {
    set((s) => {
      const list = s.knowledge[kind]
      const idx = list.findIndex((e) => e.id === id)
      if (idx < 0) return s

      const prev = list[idx]
      const next = normalizeEntry({ ...prev, ...patch })

      const nextList = list.slice()
      nextList[idx] = next

      return {
        ...s,
        knowledge: { ...s.knowledge, [kind]: nextList },
      }
    })
  },

  toggleEntryEnabled: (kind, id) => {
    const entry = get().knowledge[kind].find((e) => e.id === id)
    if (!entry) return
    get().patchEntry(kind, id, { enabled: !entry.enabled })
  },

  // toggleEntryCollapsed removed

  moveEntry: (kind, fromIndex, toIndex) => {
    set((s) => {
      const list = s.knowledge[kind]
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= list.length ||
        toIndex >= list.length ||
        fromIndex === toIndex
      ) {
        return s
      }

      const nextList = list.slice()
      const [item] = nextList.splice(fromIndex, 1)
      nextList.splice(toIndex, 0, item)

      return {
        ...s,
        knowledge: { ...s.knowledge, [kind]: nextList },
      }
    })
  },

  setSaving: (saving) => set((s) => ({ ...s, saving })),
  setPublishing: (publishing) => set((s) => ({ ...s, publishing })),
  setUnpublishing: (unpublishing) => set((s) => ({ ...s, unpublishing })),
  setPublishStatus: (publishStatus) => set((s) => ({ ...s, publishStatus })),

  markSaved: () => {
    set((s) => ({ ...s, lastSavedDigest: computeDigest(s) }))
  },

  markPublished: () => {
    set((s) => ({ ...s, lastPublishedDigest: computeDigest(s) }))
  },

  markUnpublished: () => {
    set((s) => ({ ...s, lastPublishedDigest: null }))
  },

  reset: () => set(() => ({ ...createInitialState(null) })),
}))
